import {
  BarChartOutlined,
  Remove,
  TrendingDown,
  TrendingUp,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Divider,
  Grid,
  ListItemText,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { DateRangeType } from 'src/constants';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import {
  capitalize,
  formatDateByBucketUnit,
  formatNumber,
} from 'switch-fe-shared/utils/formatting';
import { UtilityTypeOrAll } from 'switch-shared/modules/crud/utility/utility.enums';
import { DurationUnit } from 'switch-shared/utils/duration.enum';

import { useDashboard } from '../../../../context/dashboardContext';
import { useGetTaggedMeterPointsQuery } from '../../../../services/switch-rest-api/meter-point.service';
import { getUtilityUnits } from '../../../../utils/formatters';
import {
  isAllSelection,
  isAllUtilitySelection,
  toConcreteUtilityType,
} from '../../../../utils/selection';
import GroupUsageTable from '../../../application-groups/components/GroupUsageTable';
import AppUsageGraph from '../../../applications/components/AppUsageGraph';
import TaggedUsageGraph from '../../../meter-points/components/TaggedUsageGraph';
import ZoneUsageGraph from '../../../zones/components/ZoneUsageGraph';

interface DateRangeOption {
  key: string;
  label: string;
  onSelect: () => void;
}

export interface Metrics {
  bulk?: number;
  subMeter?: number;
  communal?: number;
  // Per-utility breakdown when utility is 'all'
  perUtility?: {
    [utilityType: string]: {
      bulk: number;
      subMeter: number;
      communal: number;
      priorBulk?: number;
      priorSubMeter?: number;
      priorCommunal?: number;
    };
  };
}

export interface MetricsWithTrends extends Metrics {
  priorBulk?: number;
  priorSubMeter?: number;
  priorCommunal?: number;
}

export interface TrendData {
  value: number;
  percentageChange: number;
  isIncreasing: boolean;
}

interface UtilityUsageWidget {
  pageSize?: number;
  hideMetrics?: boolean;
  hideTotals?: boolean;
  utilityOverride?: UtilityTypeOrAll;
}

const UtilityUsageWidget = ({
  pageSize,
  hideMetrics = false,
  hideTotals = false,
  utilityOverride,
}: UtilityUsageWidget) => {
  /* Hooks & Use Effects */

  const theme = useTheme();

  const {
    utility,
    dateRange,
    bucketOptions,
    zone,
    zoneCount,
    setDateRange,
    setBucketOptions,
    dateRangeKey,
    setDateRangeKey,
  } = useDashboard();

  const appGroupId = useEffectiveApplicationGroupId();
  const appId = useEffectiveApplicationId();

  const effectiveUtility = utilityOverride ?? utility;

  const unit = useMemo(() => {
    return getUtilityUnits(effectiveUtility, true);
  }, [effectiveUtility]);

  /* States */

  const [metrics, setMetrics] = useState<Metrics>({
    bulk: undefined,
    subMeter: undefined,
    communal: undefined,
  });

  const [trends, setTrends] = useState<{
    bulk?: TrendData;
    subMeter?: TrendData;
    communal?: TrendData;
  }>({});

  const [tableActions, setTableActions] = useState<React.ReactNode>(null);
  const [isWidgetLoading, setIsWidgetLoading] = useState(true);
  const showTotals = !hideTotals;
  const showTrends = !hideMetrics && showTotals;

  const dateRangeOptions: DateRangeOption[] = useMemo(
    () => [
      {
        key: DateRangeType.TODAY,
        label: 'Today',
        onSelect: () => {
          setDateRange({
            start: dayjs().startOf('day').toDate(),
            end: dayjs().endOf('day').toDate(),
          });
          setBucketOptions({
            unit: DurationUnit.Hours,
            size: 1,
          });
        },
      },
      {
        key: DateRangeType.PAST_24_HOURS,
        label: '24 Hours',
        onSelect: () => {
          setDateRange({
            start: dayjs().subtract(1, 'day').toDate(),
            end: dayjs().toDate(),
          });
          setBucketOptions({
            unit: DurationUnit.Hours,
            size: 1,
          });
        },
      },
      {
        key: DateRangeType.PAST_30_DAYS,
        label: 'Past 30 Days',
        onSelect: () => {
          setDateRange({
            start: dayjs().subtract(30, 'day').toDate(),
            end: dayjs().endOf('day').toDate(),
          });
          setBucketOptions({
            unit: DurationUnit.Days,
            size: 1,
          });
        },
      },
      {
        key: DateRangeType.WEEK,
        label: 'This Week',
        onSelect: () => {
          setDateRange({
            start: dayjs().startOf('week').toDate(),
            end: dayjs().endOf('week').toDate(),
          });
          setBucketOptions({
            unit: DurationUnit.Days,
            size: 1,
          });
        },
      },
      {
        key: DateRangeType.THIS_MONTH,
        label: 'This Month',
        onSelect: () => {
          setDateRange({
            start: dayjs().startOf('month').toDate(),
            end: dayjs().endOf('month').toDate(),
          });
          setBucketOptions({
            unit: DurationUnit.Days,
            size: 1,
          });
        },
      },
      {
        key: DateRangeType.LAST_MONTH,
        label: 'Last Month',
        onSelect: () => {
          setDateRange({
            start: dayjs().subtract(1, 'month').startOf('month').toDate(),
            end: dayjs().subtract(1, 'month').endOf('month').toDate(),
          });
          setBucketOptions({
            unit: DurationUnit.Days,
            size: 1,
          });
        },
      },
    ],
    [setDateRange, setBucketOptions]
  );

  const defaultOption = dateRangeOptions[3];

  /* API Calls */

  const { data: taggedMeterPoints } = useGetTaggedMeterPointsQuery(
    {
      appId: appId ?? '',
      utilityType: toConcreteUtilityType(effectiveUtility),
      limit: 0,
    },
    { skip: appId == null || isAllSelection(appId) }
  );

  /* Functions & Functional Components */

  const getSubheader = () => {
    let base = ``;

    if (appId == null || isAllSelection(appId)) {
      base += 'Per application for ';
    } else if (!!zoneCount && zone == null) {
      base += `Per zone for `;
    }

    base += `${formatDateByBucketUnit(
      dateRange.start,
      bucketOptions.unit,
      true
    )} -  ${formatDateByBucketUnit(dateRange.end, bucketOptions.unit, true)}${zone?.name ? ` in ${zone.name}` : ''}`;

    return base;
  };

  const getTitle = () => {
    if (isAllUtilitySelection(effectiveUtility)) {
      return 'Combined Utility Usage';
    }
    return `${capitalize(effectiveUtility)} Usage`;
  };

  const handleMetrics = useCallback(
    (newMetrics: Metrics, priorMetrics?: Metrics) => {
      setMetrics(newMetrics);

      if (priorMetrics) {
        // Calculate trends
        const calculateTrend = (
          current?: number,
          prior?: number
        ): TrendData | undefined => {
          if (current === undefined || prior === undefined || prior === 0)
            return undefined;

          const percentageChange = ((current - prior) / prior) * 100;
          return {
            value: current,
            percentageChange,
            isIncreasing: percentageChange > 0,
          };
        };

        setTrends({
          bulk: calculateTrend(newMetrics.bulk, priorMetrics.bulk),
          subMeter: calculateTrend(newMetrics.subMeter, priorMetrics.subMeter),
          communal: calculateTrend(newMetrics.communal, priorMetrics.communal),
        });
      } else {
        setTrends({});
      }
    },
    []
  );

  const handleSelectDateRange = (key: string) => {
    const dateRangeOption = dateRangeOptions.find(
      (option) => option.key === key
    );
    if (dateRangeOption) {
      dateRangeOption.onSelect();
      setDateRangeKey(key as DateRangeType);
    }
  };

  const calculatePriorPeriodDates = () => {
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const durationInMs = endDate.diff(startDate);

    const priorStartDate = startDate.subtract(durationInMs, 'millisecond');
    const priorEndDate = endDate.subtract(durationInMs, 'millisecond');

    return { priorStartDate, priorEndDate };
  };

  const TrendIndicator = ({ trend }: { trend?: TrendData }) => {
    if (!trend) return null;

    const { percentageChange, isIncreasing } = trend;
    const absChange = Math.abs(percentageChange);

    const getTooltipContent = () => {
      const { priorStartDate, priorEndDate } = calculatePriorPeriodDates();
      const direction = isIncreasing ? 'increase' : 'decrease';
      const changeText =
        absChange < 0.1
          ? 'No significant change'
          : `${absChange.toFixed(1)}% ${direction}`;

      const priorPeriodText = `${formatDateByBucketUnit(
        priorStartDate.toDate(),
        bucketOptions.unit,
        true
      )} - ${formatDateByBucketUnit(
        priorEndDate.toDate(),
        bucketOptions.unit,
        true
      )}`;

      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {changeText}
          </Typography>
          <Typography variant='body1' sx={{ display: 'block' }}>
            vs. prior period ({priorPeriodText})
          </Typography>
        </Box>
      );
    };

    // If change is very small (less than 0.1%), show neutral
    if (absChange < 0.1) {
      return (
        <Tooltip title={getTooltipContent()}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              ml: 1,
              color: theme.palette.text.secondary,
              cursor: 'help',
            }}
          >
            <Remove fontSize='small' />
            <Typography variant='caption' sx={{ ml: 0.5 }}>
              0%
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={getTooltipContent()}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            ml: 1,
            color: isIncreasing
              ? theme.palette.success.main
              : theme.palette.error.main,
            cursor: 'help',
          }}
        >
          {isIncreasing ? (
            <TrendingUp fontSize='small' color='info' />
          ) : (
            <TrendingDown fontSize='small' color='info' />
          )}
          <Typography variant='caption' sx={{ ml: 0.5 }}>
            {absChange.toFixed(1)}%
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  return (
    <WidgetCard
      title={getTitle()}
      subheader={getSubheader()}
      allowTwoRowHeader={true}
      cardContentProps={
        appId === null || isAllSelection(appId)
          ? {
              sx: {
                px: '0',
                paddingBottom: showTotals ? '' : '0px !important',
              },
            }
          : { sx: { paddingBottom: showTotals ? '' : '0px !important' } }
      }
      avatar={
        <Box
          sx={{
            padding: `${theme.tokens.spacing.sm}px`,
            height: `${theme.tokens.spacing.sm * 5}px`,
            width: `${theme.tokens.spacing.sm * 5}px`,
            mx: `-${theme.tokens.spacing.sm}px`,
            borderRadius: theme.tokens.borderRadius.full,
            background: theme.tokens.colors.card,
            boxShadow: theme.tokens.shadows.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <BarChartOutlined />
        </Box>
      }
      action={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Autocomplete
            openOnFocus
            disableClearable
            value={
              dateRangeOptions.find((ele) => ele.key === dateRangeKey) ??
              defaultOption
            }
            onChange={(_, value) => {
              handleSelectDateRange(value.key);
            }}
            options={dateRangeOptions}
            getOptionLabel={(option) => option.label}
            sx={{ minWidth: 150, alignSelf: 'end' }}
            size='small'
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label='Date Range'
                sx={{
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: theme.palette.text.primary,
                    opacity: 1,
                  },
                }}
                InputLabelProps={{
                  sx: { background: 'transparent !important' },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: params.InputProps.endAdornment,
                }}
              />
            )}
          />
          {(appId == null || isAllSelection(appId)) && tableActions}
        </Box>
      }
    >
      <Grid container flexDirection={'column'} height={'100%'}>
        <Grid item xs>
          {appId == null || isAllSelection(appId) ? (
            <GroupUsageTable
              handleMetrics={handleMetrics}
              appGroupId={appGroupId}
              utility={effectiveUtility}
              startDate={dateRange.start}
              endDate={dateRange.end}
              bucketSize={bucketOptions.size}
              bucketUnit={bucketOptions.unit}
              pageSize={pageSize}
              zoneId={zone?._id}
              includePriorPeriod={showTrends}
              onActionsReady={setTableActions}
              onLoadingChange={setIsWidgetLoading}
            />
          ) : (
            <>
              {!!zoneCount && zone == null ? (
                <ZoneUsageGraph
                  handleMetrics={handleMetrics}
                  dateRange={dateRange}
                  bucketOptions={bucketOptions}
                  utility={effectiveUtility}
                  onLoadingChange={setIsWidgetLoading}
                />
              ) : (
                <>
                  {(taggedMeterPoints?.totalResults ?? 0) > 0
                    ? appId && (
                        <TaggedUsageGraph
                          handleMetrics={handleMetrics}
                          appId={appId}
                          utilityType={effectiveUtility}
                          startDate={dateRange.start}
                          endDate={dateRange.end}
                          bucketSize={bucketOptions.size}
                          bucketUnit={bucketOptions.unit}
                          zoneId={zone?._id}
                          onLoadingChange={setIsWidgetLoading}
                        />
                      )
                    : appId && (
                        <AppUsageGraph
                          handleMetrics={handleMetrics}
                          appId={appId}
                          utilityType={effectiveUtility}
                          startDate={dateRange.start}
                          endDate={dateRange.end}
                          bucketSize={bucketOptions.size}
                          bucketUnit={bucketOptions.unit}
                          zoneId={zone?._id}
                          includePriorPeriod={showTrends}
                          onLoadingChange={setIsWidgetLoading}
                        />
                      )}
                </>
              )}
            </>
          )}
        </Grid>
        {showTotals && (
          <Grid
            item
            container
            justifyContent={'space-around'}
            sx={{
              paddingTop: `${theme.tokens.spacing.lg}px`,
              px: `${theme.tokens.spacing.lg}px`,
            }}
          >
            {isAllUtilitySelection(effectiveUtility) && metrics.perUtility ? (
              // Show per-utility breakdown when utility is 'all'
              <>
                {Object.entries(metrics.perUtility).map(
                  ([utilityType, utilityMetrics], utilityIndex, array) => {
                    const utilityUnit = getUtilityUnits(
                      utilityType as any,
                      true
                    );
                    return (
                      <Fragment key={utilityType}>
                        <Grid
                          item
                          container
                          justifyContent={'space-around'}
                          sx={{ width: '100%' }}
                        >
                          <Grid item width={'33%'}>
                            <ListItemText
                              sx={{
                                marginY: 'auto',
                                ml: `${theme.tokens.spacing.lg}px`,
                              }}
                              primary={
                                <Typography
                                  variant='body1'
                                  sx={{
                                    color: theme.tokens.colors.mutedForeground,
                                  }}
                                >
                                  {isWidgetLoading ? (
                                    <Skeleton
                                      variant='text'
                                      width={100}
                                      height={22}
                                      sx={{ mr: theme.tokens.spacing.sm }}
                                    />
                                  ) : (
                                    `Total ${capitalize(utilityType)} Bulk`
                                  )}
                                </Typography>
                              }
                              secondary={
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <Typography
                                    variant='h3'
                                    fontWeight={600}
                                    style={{ opacity: 0.7 }}
                                  >
                                    {isWidgetLoading ? (
                                      <Skeleton
                                        variant='text'
                                        width={180}
                                        height={28}
                                      />
                                    ) : (
                                      `${formatNumber(utilityMetrics.bulk)} ${utilityUnit}`
                                    )}
                                  </Typography>
                                  {showTrends &&
                                    utilityMetrics.priorBulk !== undefined && (
                                      <TrendIndicator
                                        trend={
                                          utilityMetrics.bulk !== 0 ||
                                          utilityMetrics.priorBulk !== 0
                                            ? {
                                                value: utilityMetrics.bulk,
                                                percentageChange:
                                                  utilityMetrics.priorBulk === 0
                                                    ? 0
                                                    : ((utilityMetrics.bulk -
                                                        utilityMetrics.priorBulk) /
                                                        utilityMetrics.priorBulk) *
                                                      100,
                                                isIncreasing:
                                                  utilityMetrics.bulk >
                                                  utilityMetrics.priorBulk,
                                              }
                                            : undefined
                                        }
                                      />
                                    )}
                                </Box>
                              }
                              secondaryTypographyProps={{ component: 'div' }}
                            />
                          </Grid>
                          <Divider orientation='vertical' flexItem />
                          <Grid item width={'33%'}>
                            <ListItemText
                              sx={{
                                marginY: 'auto',
                                ml: `${theme.tokens.spacing.lg}px`,
                              }}
                              primary={
                                <Typography
                                  variant='body1'
                                  sx={{
                                    color: theme.tokens.colors.mutedForeground,
                                  }}
                                >
                                  {isWidgetLoading ? (
                                    <Skeleton
                                      variant='text'
                                      width={100}
                                      height={22}
                                      sx={{ mr: theme.tokens.spacing.sm }}
                                    />
                                  ) : (
                                    `Total ${capitalize(utilityType)} Sub-Meter`
                                  )}
                                </Typography>
                              }
                              secondary={
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <Typography
                                    variant='h3'
                                    fontWeight={600}
                                    style={{ opacity: 0.7 }}
                                  >
                                    {isWidgetLoading ? (
                                      <Skeleton
                                        variant='text'
                                        width={180}
                                        height={28}
                                      />
                                    ) : (
                                      `${formatNumber(utilityMetrics.subMeter)} ${utilityUnit}`
                                    )}
                                  </Typography>
                                  {showTrends &&
                                    utilityMetrics.priorSubMeter !==
                                      undefined && (
                                      <TrendIndicator
                                        trend={
                                          utilityMetrics.subMeter !== 0 ||
                                          utilityMetrics.priorSubMeter !== 0
                                            ? {
                                                value: utilityMetrics.subMeter,
                                                percentageChange:
                                                  utilityMetrics.priorSubMeter ===
                                                  0
                                                    ? 0
                                                    : ((utilityMetrics.subMeter -
                                                        utilityMetrics.priorSubMeter) /
                                                        utilityMetrics.priorSubMeter) *
                                                      100,
                                                isIncreasing:
                                                  utilityMetrics.subMeter >
                                                  utilityMetrics.priorSubMeter,
                                              }
                                            : undefined
                                        }
                                      />
                                    )}
                                </Box>
                              }
                              secondaryTypographyProps={{ component: 'div' }}
                            />
                          </Grid>
                          <Divider orientation='vertical' flexItem />
                          <Grid item width={'33%'}>
                            <ListItemText
                              sx={{
                                marginY: 'auto',
                                ml: `${theme.tokens.spacing.lg}px`,
                              }}
                              primary={
                                <Typography
                                  variant='body1'
                                  sx={{
                                    color: theme.tokens.colors.mutedForeground,
                                  }}
                                >
                                  {isWidgetLoading ? (
                                    <Skeleton
                                      variant='text'
                                      width={100}
                                      height={22}
                                      sx={{ mr: theme.tokens.spacing.sm }}
                                    />
                                  ) : (
                                    `Total ${capitalize(utilityType)} Unmetered`
                                  )}
                                </Typography>
                              }
                              secondary={
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <Typography
                                    variant='h3'
                                    fontWeight={600}
                                    style={{ opacity: 0.7 }}
                                  >
                                    {isWidgetLoading ? (
                                      <Skeleton
                                        variant='text'
                                        width={180}
                                        height={28}
                                      />
                                    ) : (
                                      `${formatNumber(utilityMetrics.communal)} ${utilityUnit}`
                                    )}
                                  </Typography>
                                  {showTrends &&
                                    utilityMetrics.priorCommunal !==
                                      undefined && (
                                      <TrendIndicator
                                        trend={
                                          utilityMetrics.communal !== 0 ||
                                          utilityMetrics.priorCommunal !== 0
                                            ? {
                                                value: utilityMetrics.communal,
                                                percentageChange:
                                                  utilityMetrics.priorCommunal ===
                                                  0
                                                    ? 0
                                                    : ((utilityMetrics.communal -
                                                        utilityMetrics.priorCommunal) /
                                                        utilityMetrics.priorCommunal) *
                                                      100,
                                                isIncreasing:
                                                  utilityMetrics.communal >
                                                  utilityMetrics.priorCommunal,
                                              }
                                            : undefined
                                        }
                                      />
                                    )}
                                </Box>
                              }
                              secondaryTypographyProps={{ component: 'div' }}
                            />
                          </Grid>
                        </Grid>
                        {utilityIndex < array.length - 1 && (
                          <Grid item sx={{ width: '100%', my: 2 }}>
                            <Divider />
                          </Grid>
                        )}
                      </Fragment>
                    );
                  }
                )}
              </>
            ) : (
              // Show bulk/sub-meter/unmetered breakdown for single utility
              <>
                <Grid item sx={{ width: '33%' }}>
                  <ListItemText
                    sx={{
                      marginY: 'auto',
                      ml: `${theme.tokens.spacing.lg}px`,
                    }}
                    primary={
                      <Typography
                        variant='body1'
                        sx={{ color: theme.tokens.colors.mutedForeground }}
                      >
                        {isWidgetLoading ? (
                          <Skeleton
                            variant='text'
                            width={100}
                            height={22}
                            sx={{ mr: theme.tokens.spacing.sm }}
                          />
                        ) : (
                          `Total Bulk`
                        )}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant='h3'
                          fontWeight={600}
                          style={{ opacity: 0.7 }}
                        >
                          {isWidgetLoading ? (
                            <Skeleton variant='text' width={180} height={28} />
                          ) : (
                            `${metrics.bulk !== undefined ? formatNumber(metrics.bulk) : '0'} ${unit}`
                          )}
                        </Typography>
                        {showTrends && <TrendIndicator trend={trends.bulk} />}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </Grid>
                <Divider orientation='vertical' flexItem />
                <Grid item sx={{ width: '33%' }}>
                  <ListItemText
                    sx={{
                      marginY: 'auto',
                      ml: `${theme.tokens.spacing.lg}px`,
                    }}
                    primary={
                      <Typography
                        variant='body1'
                        sx={{ color: theme.tokens.colors.mutedForeground }}
                      >
                        {isWidgetLoading ? (
                          <Skeleton
                            variant='text'
                            width={100}
                            height={22}
                            sx={{ mr: theme.tokens.spacing.sm }}
                          />
                        ) : (
                          `Total Sub-Meter`
                        )}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant='h3'
                          fontWeight={600}
                          style={{ opacity: 0.7 }}
                        >
                          {isWidgetLoading ? (
                            <Skeleton variant='text' width={180} height={28} />
                          ) : (
                            `${metrics.subMeter !== undefined ? formatNumber(metrics.subMeter) : '0'} ${unit}`
                          )}
                        </Typography>
                        {showTrends && (
                          <TrendIndicator trend={trends.subMeter} />
                        )}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </Grid>
                <Divider orientation='vertical' flexItem />
                <Grid item sx={{ width: '33%' }}>
                  <ListItemText
                    sx={{
                      marginY: 'auto',
                      ml: `${theme.tokens.spacing.lg}px`,
                    }}
                    primary={
                      <Typography
                        variant='body1'
                        sx={{ color: theme.tokens.colors.mutedForeground }}
                      >
                        {isWidgetLoading ? (
                          <Skeleton
                            variant='text'
                            width={100}
                            height={22}
                            sx={{ mr: theme.tokens.spacing.sm }}
                          />
                        ) : (
                          `Total Unmetered`
                        )}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant='h3'
                          fontWeight={600}
                          style={{ opacity: 0.7 }}
                        >
                          {isWidgetLoading ? (
                            <Skeleton variant='text' width={180} height={28} />
                          ) : (
                            `${metrics.communal !== undefined ? formatNumber(metrics.communal) : '0'} ${unit}`
                          )}
                        </Typography>
                        {showTrends && (
                          <TrendIndicator trend={trends.communal} />
                        )}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        )}
      </Grid>
    </WidgetCard>
  );
};

export default UtilityUsageWidget;
